import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Input, Textarea } from 'components/FormFields';
import { Icon, inkA, orange, tokens } from 'components/v3';
import { LIMITS } from '../../shared/fieldTypes';
import { useBuilder } from '../BuilderContext';

// The canvas sheet's head: an orange accent strip, the account tag, and
// the form's title and description edited in place (FormFields inputs
// styled flat). Edits commit on blur; an empty title becomes
// "Untitled Form".

const flat = (p) => `
  && {
    background: transparent;
    border-color: transparent;
    box-shadow: none;
    padding: 4px 6px;
    margin: 0 -6px;
    width: calc(100% + 12px);
    color: ${p.$color};
    font-family: inherit;
  }

  &&:hover:not(:disabled) {
    border-color: ${p.$hover};
  }

  &&:focus {
    border-color: ${orange(1)};
    background: ${p.$focusBg};
  }

  &&:disabled {
    opacity: 1;
    cursor: default;
  }
`;

const TitleInput = styled(Input)`
  ${flat}
  && {
    font-size: ${(p) => (p.$mobile ? 19 : 22)}px;
    font-weight: 700;
  }
`;

const DescriptionInput = styled(Textarea)`
  ${flat}
  && {
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    min-height: 0;
  }
`;

// Local text that follows the stored value unless the admin is typing.
const useDraft = (value) => {
  const [draft, setDraft] = useState(value);
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setDraft(value);
  }, [value]);
  return {
    draft,
    setDraft,
    onFocus: () => {
      editing.current = true;
    },
    done: () => {
      editing.current = false;
    },
  };
};

const SheetHeader = ({ mobile }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { state, dispatch, readOnly, accountName } = useBuilder();
  const { name, description } = state.working;
  const title = useDraft(name);
  const desc = useDraft(description);
  const set = (key, value) => dispatch({ type: 'SET_SETTING', key, value });
  const flatProps = {
    $color: t.text,
    $hover: inkA(theme, 0.12),
    $focusBg: inkA(theme, 0.04),
  };
  const nameError = state.errors.name;

  return (
    <div
      style={{
        position: 'relative',
        padding: mobile ? '20px 16px 16px' : '26px 28px 20px',
        borderBottom: '1px solid ' + t.border,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: orange(1),
        }}
      />
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: t.primary,
          marginBottom: 6,
        }}
      >
        <Icon name="flag" size={13} color="currentColor" />
        {accountName || 'Form'}
      </div>
      <TitleInput
        aria-label="Form title"
        value={title.draft}
        maxLength={LIMITS.formName}
        disabled={readOnly}
        error={!!nameError}
        helperText={nameError || undefined}
        onFocus={title.onFocus}
        onChange={(e) => title.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
        }}
        onBlur={() => {
          title.done();
          const next = title.draft.trim() || 'Untitled Form';
          title.setDraft(next);
          if (next !== name) set('name', next);
        }}
        $mobile={mobile}
        {...flatProps}
      />
      <div style={{ marginTop: 4 }}>
        <DescriptionInput
          aria-label="Form description"
          rows={2}
          placeholder="Add a short description…"
          value={desc.draft}
          maxLength={LIMITS.formDescription}
          disabled={readOnly}
          onFocus={desc.onFocus}
          onChange={(e) => desc.setDraft(e.target.value)}
          onBlur={() => {
            desc.done();
            const next = desc.draft.trim();
            desc.setDraft(next);
            if (next !== description) set('description', next);
          }}
          {...flatProps}
        />
      </div>
    </div>
  );
};

SheetHeader.propTypes = { mobile: PropTypes.bool };
SheetHeader.defaultProps = { mobile: false };

export default SheetHeader;
